import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from './Toast';
import { PaymentMethod } from './PaymentMethods';
import './MobilePayment.css';

interface MobilePaymentProps {
  paymentMethod: PaymentMethod;
  totalAmount: number;
  currency: string;
  sessionId: string;
  onSuccess: () => void;
  onCancel: () => void;
  onError: (error: string) => void;
}

const MobilePayment: React.FC<MobilePaymentProps> = ({
  paymentMethod,
  totalAmount,
  currency,
  sessionId,
  onSuccess,
  onCancel,
  onError
}) => {
  const { t } = useTranslation();
  const { toasts, removeToast, showError } = useToast();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isValidPhone, setIsValidPhone] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'input' | 'processing' | 'waiting' | 'confirming'>('input');
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const [paymentReference, setPaymentReference] = useState<string>('');

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  const validatePhoneNumber = (phone: string) => {
    // Portuguese mobile number validation
    const phoneRegex = /^(\+351|351)?[269]\d{8}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Add country code if not present
    let formattedDigits = digits;
    if (!digits.startsWith('351') && !digits.startsWith('+351')) {
      if (digits.length === 9 && (digits.startsWith('2') || digits.startsWith('6') || digits.startsWith('9'))) {
        formattedDigits = '351' + digits;
      }
    }
    
    // Format as +351 XXX XXX XXX
    if (formattedDigits.length >= 12) {
      const countryCode = formattedDigits.substring(0, 3);
      const firstPart = formattedDigits.substring(3, 6);
      const secondPart = formattedDigits.substring(6, 9);
      const thirdPart = formattedDigits.substring(9, 12);
      return `+${countryCode} ${firstPart} ${secondPart} ${thirdPart}`;
    } else if (formattedDigits.length >= 9) {
      const firstPart = formattedDigits.substring(0, 3);
      const secondPart = formattedDigits.substring(3, 6);
      const thirdPart = formattedDigits.substring(6, 9);
      return `+${firstPart} ${secondPart} ${thirdPart}`;
    }
    
    return `+${formattedDigits}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = formatPhoneNumber(value);
    setPhoneNumber(formatted);
    setIsValidPhone(validatePhoneNumber(formatted));
  };

  const handleConfirmPayment = async () => {
    if (!isValidPhone) {
      showError(t('payment.invalidPhoneNumber'));
      return;
    }

    setPaymentStatus('processing');
    
    try {
      // Simulate API call to initiate mobile payment
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock payment reference
      const reference = `REF${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
      setPaymentReference(reference);
      
      setPaymentStatus('waiting');
      setTimeLeft(300); // Reset timer
      
      // Start polling for payment confirmation
      startPaymentPolling();
      
    } catch (error) {
      showError(t('payment.initiationError'));
      setPaymentStatus('input');
    }
  };

  const startPaymentPolling = () => {
    const pollInterval = setInterval(() => {
      // Simulate random payment confirmation after some time
      const shouldConfirm = Math.random() > 0.7; // 30% chance each poll
      
      if (shouldConfirm) {
        clearInterval(pollInterval);
        setPaymentStatus('confirming');
        
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    }, 3000);

    // Clean up interval after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      if (paymentStatus === 'waiting') {
        onError(t('payment.timeoutError'));
      }
    }, 300000);
  };

  // Timer countdown effect
  useEffect(() => {
    if (paymentStatus === 'waiting' && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (paymentStatus === 'waiting' && timeLeft === 0) {
      onError(t('payment.timeoutError'));
    }
  }, [paymentStatus, timeLeft, onError, t]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getBrandName = () => {
    return paymentMethod.type === 'mbway' ? 'MB Way' : 'Bizum';
  };

  const getBrandLogo = () => {
    return paymentMethod.type === 'mbway' ? '🏧' : '💰';
  };

  const getInstructions = () => {
    if (paymentMethod.type === 'mbway') {
      return t('payment.mbwayInstructions');
    }
    return t('payment.bizumInstructions');
  };

  // Input Phase
  if (paymentStatus === 'input') {
    return (
      <div className="mobile-payment">
        <div className="payment-container">
          {/* Header */}
          <div className="payment-header">
            <div className="brand-header">
              <span className="brand-logo">{getBrandLogo()}</span>
              <h2>{getBrandName()}</h2>
            </div>
            <div className="amount-display">
              {formatPrice(totalAmount, currency)}
            </div>
          </div>

          {/* Instructions */}
          <div className="payment-instructions">
            <p>{getInstructions()}</p>
          </div>

          {/* Phone Input */}
          <div className="phone-input-section">
            <label htmlFor="phone" className="input-label">
              {t('payment.phoneNumber')} *
            </label>
            
            <div className="phone-input-container">
              <div className="country-flag">🇵🇹</div>
              <input
                id="phone"
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder="XXX XXX XXX"
                className={`phone-input ${isValidPhone ? 'valid' : phoneNumber ? 'invalid' : ''}`}
                maxLength={17}
              />
              {isValidPhone && (
                <div className="input-check">✅</div>
              )}
            </div>
            
            <div className="input-help">
              {paymentMethod.type === 'mbway' 
                ? t('payment.mbwayPhoneHelp')
                : t('payment.bizumPhoneHelp')
              }
            </div>
          </div>

          {/* Security Info */}
          <div className="security-notice">
            <div className="security-icon">🔒</div>
            <div className="security-text">
              <h4>{t('payment.securePayment')}</h4>
              <p>{t('payment.securityNotice')}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="payment-actions">
            <button onClick={onCancel} className="btn-secondary">
              {t('common.back')}
            </button>
            
            <button 
              onClick={handleConfirmPayment}
              disabled={!isValidPhone}
              className="btn-primary"
            >
              {t('payment.confirm')}
            </button>
          </div>
        </div>

        <ToastContainer messages={toasts} onClose={removeToast} />
      </div>
    );
  }

  // Processing Phase
  if (paymentStatus === 'processing') {
    return (
      <div className="mobile-payment processing">
        <div className="payment-container">
          <div className="processing-header">
            <div className="processing-spinner"></div>
            <h2>{t('payment.processingPayment')}</h2>
          </div>
          
          <div className="processing-info">
            <p>{t('payment.initiatingPayment')}</p>
            <div className="brand-info">
              <span className="brand-logo">{getBrandLogo()}</span>
              <span>{getBrandName()}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Waiting for confirmation phase
  if (paymentStatus === 'waiting') {
    return (
      <div className="mobile-payment waiting">
        <div className="payment-container">
          {/* Header */}
          <div className="waiting-header">
            <div className="phone-icon">📱</div>
            <h2>{t('payment.waitingConfirmation')}</h2>
          </div>

          {/* Payment Details */}
          <div className="payment-details">
            <div className="detail-row">
              <span className="label">{t('payment.amount')}:</span>
              <span className="value amount">{formatPrice(totalAmount, currency)}</span>
            </div>
            <div className="detail-row">
              <span className="label">{t('payment.phoneNumber')}:</span>
              <span className="value">{phoneNumber}</span>
            </div>
            <div className="detail-row">
              <span className="label">{t('payment.reference')}:</span>
              <span className="value reference">{paymentReference}</span>
            </div>
          </div>

          {/* Timer */}
          <div className="payment-timer">
            <div className="timer-circle">
              <div className="timer-text">
                <span className="time">{formatTime(timeLeft)}</span>
                <span className="label">{t('payment.remaining')}</span>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="confirmation-instructions">
            <h3>{t('payment.confirmationSteps')}</h3>
            <div className="steps-list">
              <div className="step-item">
                <span className="step-number">1</span>
                <span className="step-text">
                  {paymentMethod.type === 'mbway' 
                    ? t('payment.mbwayStep1')
                    : t('payment.bizumStep1')
                  }
                </span>
              </div>
              <div className="step-item">
                <span className="step-number">2</span>
                <span className="step-text">
                  {paymentMethod.type === 'mbway' 
                    ? t('payment.mbwayStep2')
                    : t('payment.bizumStep2')
                  }
                </span>
              </div>
              <div className="step-item">
                <span className="step-number">3</span>
                <span className="step-text">
                  {t('payment.confirmTransaction')}
                </span>
              </div>
            </div>
          </div>

          {/* Cancel Option */}
          <div className="cancel-section">
            <button onClick={onCancel} className="btn-text">
              {t('payment.cancelPayment')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Confirming phase
  if (paymentStatus === 'confirming') {
    return (
      <div className="mobile-payment confirming">
        <div className="payment-container">
          <div className="confirmation-animation">
            <div className="success-icon">✅</div>
            <h2>{t('payment.paymentConfirmed')}</h2>
          </div>
          
          <div className="confirmation-info">
            <p>{t('payment.redirectingToSuccess')}</p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default MobilePayment;